<script lang="ts" setup>
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import NoContentUtil from "@/features/experience-shared/components/NoContentUtil.vue";
import { Option } from "@/types/Option";
import FieldHealthAndSafetyModal from "./FieldHealthAndSafetyModal.vue";

export interface Props {
  modelValue: string[];
  disabled?: boolean;
  options: Option[];
  readonly?: boolean;
}

const props = defineProps<Props>();
const selectedOptions = defineModel<string[]>("modelValue", { required: true });

const showModal = ref(false);
const isEmpty = computed(() => props.modelValue.length === 0);
const selectedOptionsLabels = computed(() =>
  selectedOptions.value
    .map((el) => props.options.find((opt) => opt.value === el)?.label)
    .filter((label): label is string => !!label)
    .sort()
    .join(", ")
);
</script>

<template>
  <NoContentUtil v-if="readonly && isEmpty" />
  <span v-else>
    <div
      v-if="isEmpty"
      class="w-full max-w-[430px] p-5 border border-gray-300 rounded-md font-bold text-sm flex justify-between items-center"
      :class="{ 'text-gray-500': disabled }"
      :disabled="disabled"
      data-testid="experience-health-and-safety"
    >
      {{ $t("experience.health-and-safety-title") }}
      <NovaButton
        v-if="!disabled"
        size="sm"
        data-testid="experience-health-and-safety-show-btn"
        @click="showModal = true"
      >
        {{ $t("experience.health-and-safety-browse") }}
      </NovaButton>
    </div>

    <div
      v-else
      class="w-[430px] p-5 border border-gray-300 rounded-md font-bold text-sm"
      :class="{ 'text-gray-500': disabled }"
      :disabled="disabled"
      data-testid="experience-health-and-safety-edit"
    >
      <div>
        <span class="flex items-center justify-between">
          <h3>{{ $t("experience.health-and-safety-title") }}</h3>
          <NovaButton
            v-if="!disabled && !readonly"
            variant="text"
            size="sm"
            class="font-bold text-sm"
            data-testid="experience-health-and-safety-edit-btn"
            @click="showModal = true"
          >
            <NovaIcon name="edit" :size="18" class="mr-1" />
            {{ $t("common.edit") }}</NovaButton
          >
        </span>
        <p class="mt-1 font-normal text-sm">{{ selectedOptionsLabels }}</p>
      </div>
    </div>

    <FieldHealthAndSafetyModal v-model:is-open="showModal" v-model="selectedOptions" :options />
  </span>
</template>
